import React, { useMemo } from 'react';
import { useDry } from '../dry/DryContext';
import { DEFAULT_LABELS, LabelKey, LabelForms } from './labels';

// useLabel resolves a platform noun against the tenant's terminology
// overrides (if any), falling back to the English default registry.
//
// Examples:
//   useLabel('site')                          → "Site"   (default)
//                                             → "Building"  (tenant overrode)
//   useLabel('site', { form: 'plural' })      → "Buildings"
//   useLabel('asset', { case: 'lower' })      → "zone"
//   useLabel('asset', { form: 'plural', case: 'lower' }) → "zones"
//
// The hook re-renders the consumer whenever tenant labels change
// (Settings → Terminology Save), so no manual subscription is needed.
export function useLabel(
  key: LabelKey,
  opts: { form?: 'singular' | 'plural'; case?: 'title' | 'lower' } = {},
): string {
  const { labels: overrides } = useDry();
  const merged = useMemo<Record<LabelKey, LabelForms>>(() => {
    const out = { ...DEFAULT_LABELS } as Record<LabelKey, LabelForms>;
    for (const [k, v] of Object.entries(overrides || {})) {
      if (k in DEFAULT_LABELS && v?.singular && v?.plural) {
        out[k as LabelKey] = v as LabelForms;
      }
    }
    return out;
  }, [overrides]);

  const form = opts.form ?? 'singular';
  const raw = merged[key]?.[form] ?? DEFAULT_LABELS[key][form];
  return opts.case === 'lower' ? raw.toLowerCase() : raw;
}

// Component-friendly wrapper. Renders as inline text.
// <Label k="site" />              → "Site"
// <Label k="site" form="plural" /> → "Sites"
// <Label k="asset" lower />        → "asset"
export const Label: React.FC<{
  k: LabelKey;
  form?: 'singular' | 'plural';
  lower?: boolean;
}> = ({ k, form, lower }) => {
  const text = useLabel(k, { form, case: lower ? 'lower' : 'title' });
  return <>{text}</>;
};

// Convenience selector for code that wants the full {singular, plural}
// pair (e.g., generating both forms for a sidebar tooltip).
export function useLabelForms(key: LabelKey): LabelForms {
  const singular = useLabel(key, { form: 'singular' });
  const plural = useLabel(key, { form: 'plural' });
  return { singular, plural };
}
